import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { GOOGLE_MAPS_CONFIG, GoogleMapsConfig } from './google-maps-loader.config';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private loadPromise: Promise<void> | null = null;
  private isBrowser: boolean;

  constructor(
    @Inject(GOOGLE_MAPS_CONFIG) private config: GoogleMapsConfig,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  load(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      if (this.isBrowser && (window as unknown as { google?: { maps?: unknown } }).google?.maps) {
        resolve();
        return;
      }

      if (!this.isBrowser) {
        this.loadPromise = null;
        reject(new Error('Document is not available in this environment.'));
        return;
      }

      const apiKey = this.config.apiKey;
      if (!apiKey) {
        this.loadPromise = null;
        reject(new Error('GOOGLE_MAPS_CONFIG.apiKey is required to load Google Maps.'));
        return;
      }

      const libraries = this.config.libraries?.join(',') || 'places';
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries}`;
      script.async = true;
      script.defer = true;

      script.onload = () => resolve();

      // script.onerror receives a DOM Event (or nothing) — never reject with that raw value.
      // Rejecting an Event is logged as "On Body Error" with message {"isTrusted":true} / "Unknown error".
      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load Google Maps JavaScript API'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }
}
